/* @-mentions — one canonical token shared by chat (Matrix m.room.message bodies)
   and tickets (Markdown descriptions + comments).

   A mention is written into the Markdown as `@[Display Name](@user:server)`: the
   leading `@` plus an MXID target (which can't be an http(s) URL nor a lowercase
   knowledge-node slug) keep it distinct from every other link markdown.ts
   handles, so it can't collide with the existing link rules. markdown.ts turns
   the token into a pill, htmlmd.ts serializes the pill back to this token, and
   the helpers here pull the target MXIDs out for `m.mentions` and "was I
   mentioned?" checks. The Conductor parses the same shape server-side. */

/** Matches one mention token, capturing the display name (1) and the MXID (2). */
export const MENTION_RE = /@\[([^\]]+)\]\((@[^()\s]+:[^()\s]+)\)/

/** A display name made safe to sit inside a token (no brackets/parens/newlines). */
export function safeMentionName(name: string | null | undefined): string {
  return String(name || '').replace(/[[\]()]/g, '').replace(/\s+/g, ' ').trim() || 'someone'
}

/** Build the canonical mention token for a person. */
export function mentionToken(name: string, mxid: string): string {
  return `@[${safeMentionName(name)}](${mxid})`
}

/** Every MXID mentioned in a body, deduped, in order of appearance. */
export function extractMentions(body: string): string[] {
  const re = new RegExp(MENTION_RE.source, 'g')
  const ids: string[] = []
  for (let m = re.exec(body || ''); m; m = re.exec(body || '')) {
    if (!ids.includes(m[2])) ids.push(m[2])
  }
  return ids
}

/** Does this body explicitly @-mention the given MXID? */
export function mentionsInclude(body: string, mxid: string | undefined): boolean {
  if (!mxid) return false
  return extractMentions(body).includes(mxid)
}

/** The verb phrase for a ticket item in the Mentions timeline — "mentioned you",
 *  "assigned you to", "commented on", etc. Shared by the toast (mentions store)
 *  and the row (MentionsView) so they always read the same. */
export function ticketVerb(item: { kind?: 'mention' | 'sub'; action?: string }): string {
  if (item.kind !== 'sub') return 'mentioned you'
  switch (item.action) {
    case 'assigned': return 'assigned you to'
    case 'commented': return 'commented on'
    case 'moved': return 'moved'
    case 'created': return 'added you to'
    case 'transferred': return 'moved'
    default: return 'updated'
  }
}
